import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { post } from "./api.ts";
import { nativePlatform } from "./native.ts";

/**
 * "Reklamları kaldır" satın alması, mağazadan bağımsız. Play'de satın alma jetonu,
 * App Store'da imzalı işlem sunucuya doğrulatılır; hak yalnızca sunucuda yazılır.
 * Sunucu 409 dönerse satın alma başka bir oyun hesabına bağlıdır (ApiError olarak fırlar).
 */
export type PurchaseOutcome = "active" | "pending" | "cancelled";
export type RestoreOutcome = "active" | "pending" | "none";

export interface StoreBilling {
  price(): Promise<string>;
  purchase(): Promise<PurchaseOutcome>;
  restore(): Promise<RestoreOutcome>;
  /** Uygulama dışında tamamlanan satın almalar (ör. ebeveyn onayı). */
  onExternalPurchase(listener: () => void): Promise<PluginListenerHandle | null>;
}

type ServerStatus = "active" | "pending" | "revoked";

interface PlayPurchase { purchaseToken: string; state: number }
const playBilling = registerPlugin<{
  price(): Promise<{ formattedPrice: string }>;
  purchase(input: { accountId: string }): Promise<PlayPurchase>;
  restore(): Promise<{ purchases: PlayPurchase[] }>;
}>("PlayBilling");

async function verifyPlay(purchaseToken: string): Promise<ServerStatus> {
  return (await post<{ status: ServerStatus }>("/api/play/verify", { purchaseToken })).status;
}

const play: StoreBilling = {
  async price() {
    return (await playBilling.price()).formattedPrice;
  },
  async purchase() {
    const { accountId } = await post<{ accountId: string }>("/api/play/prepare");
    const purchase = await playBilling.purchase({ accountId });
    if (purchase.state !== 1) return "pending";
    return await verifyPlay(purchase.purchaseToken) === "active" ? "active" : "pending";
  },
  async restore() {
    const { purchases } = await playBilling.restore();
    if (purchases.length === 0) return "none";
    let active = false;
    for (const purchase of purchases) {
      if (await verifyPlay(purchase.purchaseToken) === "active") active = true;
    }
    return active ? "active" : "pending";
  },
  async onExternalPurchase() {
    return null;
  },
};

const appStoreBilling = registerPlugin<{
  price(): Promise<{ formattedPrice: string }>;
  purchase(input: { appAccountToken: string }): Promise<{ status: "purchased" | "pending" | "cancelled"; signedTransaction?: string }>;
  restore(): Promise<{ transactions: string[] }>;
  addListener(event: "transaction", listener: (event: { signedTransaction: string }) => void): Promise<PluginListenerHandle>;
}>("AppStoreBilling");

async function verifyAppStore(signedTransaction: string): Promise<ServerStatus> {
  return (await post<{ status: ServerStatus }>("/api/appstore/verify", { signedTransaction })).status;
}

const appStore: StoreBilling = {
  async price() {
    return (await appStoreBilling.price()).formattedPrice;
  },
  async purchase() {
    const { appAccountToken } = await post<{ appAccountToken: string }>("/api/appstore/prepare");
    const result = await appStoreBilling.purchase({ appAccountToken });
    if (result.status !== "purchased" || !result.signedTransaction) return result.status === "pending" ? "pending" : "cancelled";
    return await verifyAppStore(result.signedTransaction) === "active" ? "active" : "pending";
  },
  async restore() {
    const { transactions } = await appStoreBilling.restore();
    let active = false;
    for (const transaction of transactions) {
      if (await verifyAppStore(transaction) === "active") active = true;
    }
    return active ? "active" : "none";
  },
  async onExternalPurchase(listener) {
    return appStoreBilling.addListener("transaction", ({ signedTransaction }) => {
      void verifyAppStore(signedTransaction).then(listener, () => {});
    }).catch(() => null);
  },
};

export function storeBilling(): StoreBilling | null {
  const platform = nativePlatform();
  return platform === "android" ? play : platform === "ios" ? appStore : null;
}
